/* /admin — platform admin overview (prototype billing.js pgAdmin). */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminOverview, useAdminPayments, useAdminTenants } from "../../data/hooks";
import type { Payment } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, gDate } from "../../lib/format";
import { Button, Card, ErrorBox, Grid, Icon, Kpi, Loaded, Money, PageHeader, Skeleton } from "../../ui";
import { VerifyModal } from "./components/VerifyModal";
import { TenantStatusChip } from "./AdminTenants";

export default function AdminOverview() {
  const { L, lang } = useI18n();
  const ov = useAdminOverview();
  const pend = useAdminPayments("Pending");
  const tenants = useAdminTenants();
  const [open, setOpen] = useState<Payment | null>(null);
  return (
    <div data-testid="admin-overview">
      <PageHeader title={L("Platform admin", "إدارة المنصة")} sub={L("StartHR Technologies · internal console", "ستارت HR للتقنية · لوحة داخلية")}
        actions={<Link className="btn pri" to="/admin/payments"><Icon name="banknote" /><span>{L("Verify payments", "التحقق من المدفوعات")}</span></Link>} />
      <Loaded q={ov}>{(o) => (
        <div className="g g-4 mb">
          <Kpi label={L("Monthly recurring revenue", "الإيراد الشهري المتكرر")} value={<span data-testid="kpi-mrr">{fmtN(o.mrr, 0)}</span>} unit={L("SAR", "ر.س")} icon="chart" tone="gold" foot={L(o.active + " active companies", o.active + " منشأة نشطة")} />
          <Kpi label={L("Receipts to verify", "إيصالات بانتظار التحقق")} value={<span data-testid="kpi-pending">{o.pendingPayments}</span>} icon="banknote" tone="warn" to="/admin/payments" />
          <Kpi label={L("Customers in trial", "عملاء في التجربة")} value={<span data-testid="kpi-trials">{o.trials}</span>} icon="sparkle" tone="info" foot={L(o.tenants + " companies in total", o.tenants + " منشأة إجمالًا")} to="/admin/tenants" />
          <Kpi label={L("AI credits used (this month)", "رصيد الذكاء المستخدم (هذا الشهر)")} value={<span data-testid="kpi-ai">{fmtN(o.aiCreditsUsed, 0)}</span>} icon="zap" to="/admin/ai" />
        </div>
      )}</Loaded>
      <Grid cols="2">
        <Card title={L("Waiting for verification", "بانتظار التحقق")} testId="admin-waiting">
          {pend.error ? <ErrorBox error={pend.error} onRetry={() => pend.refetch()} /> : !pend.data ? <Skeleton /> : pend.data.length === 0 ? <p className="muted sm">{L("All caught up.", "لا يوجد شيء معلق.")}</p> : pend.data.map((p) => (
            <div key={p.id} className="list-row">
              <span className="kpi-ico warn"><Icon name="banknote" /></span>
              <div className="grow"><b>{p.tenant}</b><div className="xs muted">{p.plan} · {gDate(p.date, lang)} · <Money value={p.paid} /></div></div>
              <Button variant="sm" onClick={() => setOpen(p)} data-testid={"btn-review-" + p.id}>{L("Review", "مراجعة")}</Button>
            </div>
          ))}
        </Card>
        <Card title={L("Trials", "التجارب")} testId="admin-trials">
          {tenants.error ? <ErrorBox error={tenants.error} onRetry={() => tenants.refetch()} /> : !tenants.data ? <Skeleton /> : (() => {
            const list = tenants.data.filter((x) => /Trial/.test(x.status));
            if (!list.length) return <p className="muted sm">{L("No companies in trial.", "لا توجد منشآت في التجربة.")}</p>;
            return list.map((x) => (
              <div key={x.id} className="list-row">
                <span className="kpi-ico gold"><Icon name="building" /></span>
                <div className="grow"><b>{x.name}</b><div className="xs muted">{x.city} · {x.seats} {L("employees", "موظفًا")}</div></div>
                <TenantStatusChip status={x.status} />
              </div>
            ));
          })()}
        </Card>
      </Grid>
      {open && <VerifyModal p={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
